/**
 * usePushSoftPrompt — Phase B soft opt-in hook.
 *
 * Behaviour (per spec in /app/memory/PUSH_NOTIFICATION_PLAN.md):
 *   - Track time on page + meaningful interactions
 *   - Fire after 20s OR 2 interactions, whichever first
 *   - Never fire more than once per session
 *   - "Not now" → suppressed for 7 days (localStorage)
 *   - Browser permission already "granted"/"denied" → never fire
 *   - Push not supported by browser → never fire
 *
 * The component using this hook owns the UI; this hook only decides
 * *whether and when* to show. Call `markInteraction()` from the host
 * component on each meaningful interaction (pin tap, sheet open,
 * view-mode toggle, scroll past 50%).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { isPushSupported, permissionState } from "./push";

const SUPPRESS_KEY = "tt_push_prompt_suppress_until";
const SHOWN_THIS_SESSION_KEY = "tt_push_prompt_shown_session";
const TIME_THRESHOLD_MS = 10_000;
const INTERACTION_THRESHOLD = 1;

function suppressedUntil() {
  try {
    const raw = localStorage.getItem(SUPPRESS_KEY);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function isSuppressed() {
  return suppressedUntil() > Date.now();
}

function shownThisSession() {
  try {
    return sessionStorage.getItem(SHOWN_THIS_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function markShownThisSession() {
  try { sessionStorage.setItem(SHOWN_THIS_SESSION_KEY, "1"); } catch {}
}

/** Suppress the prompt for `days` days from now. */
export function suppressPushPrompt(days = 7) {
  try {
    localStorage.setItem(SUPPRESS_KEY, String(Date.now() + days * 86_400_000));
  } catch {}
}

/** Permanently never show again (e.g. user clicked Enable + completed). */
export function dismissPushPromptForever() {
  // 100 years out — effectively forever, but still time-bounded so a
  // future "reset prompts" admin tool can override it.
  suppressPushPrompt(365 * 100);
}

/**
 * @returns `{ visible, dismiss, accepted }`
 *   - `visible` flips true when threshold met. Stays true until dismissed.
 *   - `dismiss(reason)` hides the prompt; `reason="not_now"` suppresses
 *     for 7d, `reason="accepted"` suppresses forever, anything else is
 *     a session-only hide (e.g. user navigated away).
 *   - `markInteraction()` increments the interaction counter from the host.
 */
export function usePushSoftPrompt({ enabled = true } = {}) {
  const [visible, setVisible] = useState(false);
  const interactionsRef = useRef(0);
  const timerRef = useRef(null);
  const decidedRef = useRef(false);

  // Decide once on mount whether this user is even eligible to ever see
  // the prompt this session. Saves listeners + timers when they aren't.
  const eligibleRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (!isPushSupported()) return;
    if (shownThisSession()) return;
    if (isSuppressed()) return;
    const perm = permissionState();
    // Already granted (subscribed elsewhere) or hard-denied → never prompt.
    if (perm === "granted" || perm === "denied") return;
    eligibleRef.current = true;

    timerRef.current = window.setTimeout(() => {
      if (decidedRef.current) return;
      decidedRef.current = true;
      markShownThisSession();
      setVisible(true);
    }, TIME_THRESHOLD_MS);

    // If a sibling component (e.g. FollowButton's contextual notify
    // toast) shows a strictly-better, contextual prompt, snooze us for
    // the rest of the session — don't stack two asks on top of each
    // other.
    const onContextual = () => {
      decidedRef.current = true;
      eligibleRef.current = false;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      markShownThisSession();
      setVisible(false);
    };
    window.addEventListener("tt:contextual-notify-shown", onContextual);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      window.removeEventListener("tt:contextual-notify-shown", onContextual);
    };
  }, [enabled]);

  const markInteraction = useCallback(() => {
    if (!eligibleRef.current || decidedRef.current) return;
    interactionsRef.current += 1;
    if (interactionsRef.current >= INTERACTION_THRESHOLD) {
      decidedRef.current = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      markShownThisSession();
      setVisible(true);
    }
  }, []);

  const dismiss = useCallback((reason) => {
    setVisible(false);
    if (reason === "not_now") {
      suppressPushPrompt(7);
    } else if (reason === "accepted" || reason === "denied") {
      dismissPushPromptForever();
    }
    // any other reason → session-only hide; markShownThisSession already
    // prevents a re-show in the same tab.
  }, []);

  return { visible, markInteraction, dismiss };
}
